import { AccountView } from "../../models/view/account/account.view";
import { Origin, SyncStatusEnum } from "../../models/types/general-types";
import { SyncStatusView } from "../../models/view/sync-status/sync-status.view";
import { ConfirmationDialogService } from "../confirmation/confirmation.service";
import { TestLog } from "./test.log";
import { inject, Injectable, isDevMode, signal } from "@angular/core";
import { UserStoreService } from "../store/user/user.store.service";
import { ImportStoreService } from "../store/import/import.store.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { BootstrapSyncFetchContext } from "./bootstrap-sync-fetch.context";

export type RunningSyncState = {
  isRunning: boolean;
  origin: Origin;
  accountsCount: number;
  failedAccountsCount: number;
  isError: boolean;
  message: string;
  successfulAccountsCount: number;
};
@Injectable({
  providedIn: "root",
})
export class GlossSyncService {
  private userStore: UserStoreService;
  private importStore: ImportStoreService;
  confirmationDialog: ConfirmationDialogService;
  testLogService: TestLog;
  runningSyncState = signal<RunningSyncState>({
    isRunning: false,
    origin: undefined,
    accountsCount: 0,
    failedAccountsCount: 0,
    successfulAccountsCount: 0,
    isError: false,
    message: "",
  });

  /** When true, transaction store defers refresh until import/sync finishes. Default false. */
  private readonly _isRefreshBlocked = signal(false);
  readonly isRefreshBlocked = this._isRefreshBlocked.asReadonly();

  setRefreshBlocked(blocked: boolean): void {
    this._isRefreshBlocked.set(blocked);
  }

  /**
   * When true, sync is running as part of the background bootstrap for a beta user.
   * Sync machinery checks this to skip pizza overlays during bootstrap.
   */
  private appState = inject(AppStateStoreService);
  private bootstrapSyncFetchContext = inject(BootstrapSyncFetchContext);
  readonly isSilentBootstrap = this.appState.silentBootstrap;

  setSilentBootstrap(silent: boolean): void {
    this.appState.setSilentBootstrap(silent);
  }

  /**
   * Runs bootstrap auto-sync with a fetch timeout so a stalled HTTP call cannot block
   * {@link Bootstrap.bootstrap}'s finally block indefinitely.
   */
  runWithBootstrapFetchTimeout<T>(operation: () => Promise<T>): Promise<T | undefined> {
    return this.bootstrapSyncFetchContext.runWithTimeout(operation);
  }

  /** Clears per-session bootstrap flags (e.g. on logout). */
  resetBootstrapSession(): void {
    this.bootstrapSyncFetchContext.reset();
    this._pluginSyncDataReady.set(false);
    this.setRefreshBlocked(false);
    this.initializedRunningSyncState();
  }

  /** Set to true when plugin sync has finished and data is ready to import. */
  private _pluginSyncDataReady = signal(false);
  readonly pluginSyncDataReady = this._pluginSyncDataReady.asReadonly();

  setPluginSyncDataReady(ready: boolean): void {
    this._pluginSyncDataReady.set(ready);
  }

  constructor() {
    this.userStore = inject(UserStoreService);
    this.importStore = inject(ImportStoreService);
    this.confirmationDialog = inject(ConfirmationDialogService);
    this.testLogService = new TestLog(inject(LogService));
  }

  /** Create "syncing" status entries for accounts that are about to have data imported.
   * Called before the transaction fetch begins so the UI shows the syncing indicator immediately. */
  async markAsSyncing(accounts: AccountView[]): Promise<void> {
    if (accounts.length === 0) {
      return;
    }

    const previousLinks = new Map<string, AccountView["syncStatusLink"]>();

    try {
      const newSyncStatuses: SyncStatusView[] = [];
      const linkedAccounts = accounts.map((account) => {
        const syncStatus = new SyncStatusView({
          id: crypto.randomUUID(),
          key: SyncStatusEnum.syncing,
          vid: null,
          dc: new Date().toISOString(),
          dm: new Date().toISOString(),
          v: 2,
          err: null,
          acId: account.id,
        });
        newSyncStatuses.push(syncStatus);
        previousLinks.set(account.id, account.syncStatusLink);
        // Replace rather than append — syncStatusLink is a pointer to the current sync status,
        // not a history. Left as push(), it grows forever and the account's own cipher payload
        // eventually fails backend validation on write (observed: a heavily-synced Fiskil test
        // account hit 1,244 linked entries).
        account.syncStatusLink = [{ id: syncStatus.id }];
        return account;
      });
      await this.importStore.syncStatus.saveToVault(newSyncStatuses, true);
      await this.userStore.accounts.saveToVault(linkedAccounts, true);
    } catch (error) {
      for (const account of accounts) {
        const previous = previousLinks.get(account.id);
        if (previous) {
          account.syncStatusLink = previous;
        }
      }
      this.testLogService.testLog({ error, level: "markAsSyncing", accounts });
    }
  }

  /**
   * Claims runningSyncState's counters for a plugin sync batch about to terminateSync each of its
   * `accountsCount` accounts, the way fiskil.sync.ts/plaid.sync.ts own theirs. This signal is one
   * per session, and a finished Basiq/Fiskil/Plaid sync leaves accountsCount = N behind, so a
   * plugin batch that didn't reset it would make terminateSync's arithmetic latch isRunning true
   * (#4919). origin is set explicitly: a stale Origin.basiq would otherwise make
   * stepper.component.ts's origin === basiq effect misfire mid plugin sync. isRunning is set too,
   * as fiskil.sync.ts/plaid.sync.ts do: without it the recovery guards that check it (gloss.sync.ts,
   * manage-accounts) can't see a plugin sync until its first account has terminated, and never see
   * a single-account one at all (#5297).
   * Always pair with releasePluginSync() in a finally.
   */
  claimPluginSync(accountsCount: number): void {
    this.runningSyncState.update((state) => ({
      ...state,
      isRunning: true,
      origin: Origin.plugin,
      accountsCount,
      failedAccountsCount: 0,
      successfulAccountsCount: 0,
    }));
  }

  /**
   * Releases the counters claimed by claimPluginSync(), even if the batch unwound early. Basiq sets
   * accountsCount without zeroing the other two (basiq.job.sync.ts), so a plugin sync leaving them
   * non-zero would break a later Basiq sync's arithmetic too (#4919).
   */
  releasePluginSync(): void {
    this.runningSyncState.update((state) => ({
      ...state,
      accountsCount: 0,
      failedAccountsCount: 0,
      successfulAccountsCount: 0,
      isRunning: false,
    }));
  }

  async terminateSync(accounts: AccountView[], statusEnum: SyncStatusEnum, error?: Error) {
    try {
      const newSyncStatuses: SyncStatusView[] = [];
      const syncingAccounts = accounts.map((account) => {
        const newSyncStatus = new SyncStatusView({
          id: crypto.randomUUID(),
          key: statusEnum,
          vid: null,
          dc: new Date().toISOString(),
          dm: new Date().toISOString(),
          v: 2,
          err: {
            message: error?.message,
            stack: error?.stack,
            name: error?.name,
          },
          acId: account.id,
        });
        newSyncStatuses.push(newSyncStatus);
        // Replace rather than append — see markAsSyncing for why an unbounded syncStatusLink
        // breaks account saves once it grows large enough.
        account.syncStatusLink = [{ id: newSyncStatus.id }];
        return account;
      });

      await this.importStore.syncStatus.saveToVault(newSyncStatuses, true);
      await this.userStore.accounts.saveToVault(syncingAccounts, true);
      const runningState = this.runningSyncState();
      this.runningSyncState.set({
        ...runningState,
        // accountsCount defaults to 0 for callers that never reset it - without the > 0 guard, a
        // zero-account batch (legitimately not running) would still compute isRunning true. The
        // guard's real job is the STALE case: this signal is one per session (providedIn root),
        // so a caller that doesn't zero accountsCount/failedAccountsCount/successfulAccountsCount
        // before its own batch (see fiskil.sync.ts, plaid.sync.ts, and the plugin path in
        // plugin-account-sync.service.ts, #4919) leaves a prior sync's non-zero accountsCount behind
        // - the sum can then never re-equalise, latching isRunning stuck and permanently
        // disabling the Fiskil/Plaid recovery guards that check it.
        isRunning:
          runningState.accountsCount > 0 &&
          runningState.failedAccountsCount +
            runningState.successfulAccountsCount +
            accounts.length !==
            runningState.accountsCount,
        isError: statusEnum === SyncStatusEnum.synced ? false : true,
        message: statusEnum === SyncStatusEnum.synced ? "syncCompleted" : "somethingWentWrong",
        failedAccountsCount:
          statusEnum !== SyncStatusEnum.synced
            ? runningState.failedAccountsCount + accounts.length
            : runningState.failedAccountsCount,
        successfulAccountsCount:
          statusEnum === SyncStatusEnum.synced
            ? runningState.successfulAccountsCount + accounts.length
            : runningState.successfulAccountsCount,
      });
    } catch (error) {
      const runningState = this.runningSyncState();
      const failedAccountsCount = runningState.failedAccountsCount + accounts.length;
      this.runningSyncState.set({
        ...runningState,
        // Same counter arithmetic as the success path above, so one account's rejected save (vault
        // lock, backend validation) doesn't make the rest of the batch look finished while it is
        // still fetching/writing - the recovery guards that check isRunning would start a
        // concurrent import.
        isRunning:
          runningState.accountsCount > 0 &&
          failedAccountsCount + runningState.successfulAccountsCount !== runningState.accountsCount,
        isError: true,
        message: "somethingWentWrong",
        failedAccountsCount,
      });
      this.testLogService.testLog({
        error,
        level: "Terminating Sync(in gloss sync)",
        accounts: accounts,
      });
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  async logError(accounts: AccountView[], error: Error, level: string, status: SyncStatusEnum) {
    if (!isDevMode()) {
      return;
    }
    this.testLogService.testLog({
      level: level,
      accounts: accounts,
      error: error,
    });
    if (accounts && accounts.length > 0 && status) {
      await this.terminateSync(accounts, status, error);
    }
  }

  initializedRunningSyncState() {
    this.runningSyncState.set({
      isRunning: false,
      origin: undefined,
      accountsCount: 0,
      failedAccountsCount: 0,
      successfulAccountsCount: 0,
      isError: false,
      message: "",
    });
  }
}
